import React, { useContext, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  CircularProgress,
  Divider,
  Portal,
} from "@mui/material";

import SongList from "./songList";


import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import ScreenShareOutlinedIcon from "@mui/icons-material/ScreenShareOutlined";
import StopScreenShareOutlinedIcon from "@mui/icons-material/StopScreenShareOutlined";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PanToolIcon from "@mui/icons-material/PanTool";
import CircleIcon from "@mui/icons-material/Circle";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import PictureInPictureIcon from "@mui/icons-material/PictureInPicture";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";
import LibraryMusicIcon from "@mui/icons-material/LibraryMusic";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useTranslation } from "react-i18next";
import { SocketContext } from "../../context/socket";
import { Images } from "../../assets/common";
import SongRequestButton from "./SongRequestButton";
import SongRequestList from "./SongRequestList";

export default function StreamingControls({
  role, // "host", "cohost", or "audience"
  isOwner, // bool
  isMute,
  video,
  mute,
  disableVideo,
  isVideo,
  shareScreen,
  stopShare,
  userId,
  playlistId,
  tokenData,
  liveUsers,
  streamData,
  obs,
  isJoined,
  raisedList = [],
  mics = [],
  cameras = [],
  currentMic,
  currentCamera,
  switchAudio,
  switchVideo,
  musicTrack,
  publishMusicTrack,
  stopMusicTrack,
  showRecordButton,
  onTipHost,
  onFollowHost,
  onSubscribeHost,
  onAcceptSongRequest,
  hostId,
}) {
  const { t } = useTranslation("common");
  const socket = useContext(SocketContext).Socket;

  const [openMusic, setOpenMusic] = useState(false);
  const [openSongRequests, setOpenSongRequests] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openSetting = Boolean(anchorEl);

  const [record, setRecord] = useState(false);
  const [starting, setStarting] = useState(false);
  const [open, setOpen] = useState(false);
  const [newRaised, setNewRaise] = useState(false);

  const [availableMics, setAvailableMics] = useState([]);
  const [availableCameras, setAvailableCameras] = useState([]);
  
  // State for audience song request dialog
  const [openAudienceSongRequest, setOpenAudienceSongRequest] = useState(false);

  /**
   * Fetch available devices (microphones and cameras)
   */
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioDevices = devices.filter((device) => device.kind === "audioinput");
        const videoDevices = devices.filter((device) => device.kind === "videoinput");

        setAvailableMics(audioDevices.map((device) => ({ label: device.label, deviceId: device.deviceId })));
        setAvailableCameras(videoDevices.map((device) => ({ label: device.label, deviceId: device.deviceId })));
      } catch (error) {
        console.error("Error fetching devices:", error);
      }
    };

    fetchDevices();

    // Update devices dynamically when devices are added/removed
    navigator.mediaDevices.addEventListener("devicechange", fetchDevices);
    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", fetchDevices);
    };
  }, []);

  /**
   * Called when user wants to open mic/cam dropdown
   */
  const handleClick = (event) => {
    console.log('[StreamingControls] Settings button clicked');
    console.log('[StreamingControls] Setting anchorEl to:', event.currentTarget);
    setAnchorEl(event.currentTarget);
    console.log('[StreamingControls] openSetting will be:', Boolean(event.currentTarget));
  };

  /**
   * Called when user closes mic/cam dropdown
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Switch audio device
   */
  const handleSwitchAudio = (micLabel) => {
    console.log(`[StreamingControls] Switching audio to: ${micLabel}`);
    switchAudio(micLabel);
    handleClose();
  };

  /**
   * Switch video device
   */
  const handleSwitchVideo = (cameraLabel) => {
    console.log(`[StreamingControls] Switching video to: ${cameraLabel}`);
    switchVideo(cameraLabel);
    handleClose();
  };

/**
 * Start/Stop Recording logic
 */
const handleRecord = () => {
  setStarting(true); // Show spinner and disable button
  if (!socket || socket.disconnected) {
    console.error("[StreamingControls] Socket is not connected. Cannot start recording.");
    setStarting(false);
    return;
  }

  if (!record) {
    console.log("[StreamingControls] Starting recording...");
    const recorderUid = "your-recorder-uid"; // Replace with logic to generate or fetch recorderUid
    socket.emit(
      "start-recording",
      { channelName: playlistId, height: 1080, width: 1920, recorderUid },
      (response) => {
        if (response?.success) {
          console.log("[StreamingControls] Recording started successfully.");
          setRecord(true); // Toggle record state only after confirmation
        } else {
          console.error("[StreamingControls] Failed to start recording:", response?.error);
        }
        setStarting(false); // Hide spinner and enable button
      }
    );
  } else {
    console.log("[StreamingControls] Stopping recording...");
    socket.emit(
      "stop-recording",
      { channelName: playlistId },
      (response) => {
        if (response?.success) {
          console.log("[StreamingControls] Recording stopped successfully.");
          setRecord(false); // Toggle record state only after confirmation
        } else {
          console.error("[StreamingControls] Failed to stop recording:", response?.error);
        }
        setStarting(false); // Hide spinner and enable button
      }
    );
  }
};

  /**
   * Shared Controls for Host and Cohost
   */
  const renderHostOrCohostControls = () => (
    <Stack className="streaming_control_box" direction="row" alignItems="center">
      {/* Raised Hands List Button */}
      {raisedList.length > 0 && (
        <Button
          sx={{ height: "30px !important", position: "relative" }}
          onClick={() => {
            setOpen(!open);
            setNewRaise(false);
          }}
          variant="outlined"
          color="primary"
          startIcon={<PanToolIcon sx={{ fontSize: "13px" }} color="primary" />}
          endIcon={newRaised ? <CircleIcon className="_new_raised_dot" /> : null}
        >
          {t("stream.raisedHands")}
        </Button>
      )}

      {/* Music Player Button */}
      {(isJoined || obs) && (
        <IconButton className="btn" onClick={() => {
          console.log("[StreamingControls] Music button clicked, playlistId:", playlistId);
          setOpenMusic(true);
        }}>
          <img src={Images.allMusic} className="icon" alt="songs" />
        </IconButton>
      )}

      {/* Song Requests Button - Only for hosts */}
      {(isJoined || obs) && isOwner && (
        <IconButton 
          className="btn" 
          onClick={() => setOpenSongRequests(true)}
          title="Song Requests"
        >
          <img 
            src={Images.jukebox} 
            className="icon" 
            alt="song requests" 
            style={{ filter: 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)' }}
          />
        </IconButton>
      )}

          {/* Add SongList Component */}
    {openMusic && (
      <SongList
        open={openMusic}
        liveUsers={liveUsers}
        roomId={playlistId}
        handleClose={() => setOpenMusic(false)}
        musicTrack={musicTrack}
        publishMusicTrack={publishMusicTrack}
        stopMusicTrack={stopMusicTrack}
      />
    )}

    {/* Song Request List - Only for hosts */}
    {openSongRequests && (
      <SongRequestList
        open={openSongRequests}
        onClose={() => setOpenSongRequests(false)}
        roomId={playlistId}
        onAcceptSong={onAcceptSongRequest}
      />
    )}

{!obs && showRecordButton ? (
  starting ? (
    <IconButton className="btn">
      <CircularProgress
        sx={{
          width: "30px !important",
          height: "initial !important",
        }}
        color="primary"
      />
    </IconButton>
  ) : (
    <IconButton
      onClick={handleRecord}
      className="btn"
      disabled={starting}
      title={record ? t("stream.tooltip4") : t("stream.tooltip5")}
    >
      <RadioButtonCheckedIcon
        sx={{
          color: record ? "#E3293E" : "#ffffff",
          fontSize: "18px",
        }}
      />
    </IconButton>
  )
) : null}

      {/* Screen Sharing */}
      {!obs && (
        <IconButton
          id="_share_screen_btn"
          className="btn"
          onClick={() => (isVideo ? shareScreen() : stopShare())}
          title={isVideo ? t("stream.tooltip6") : t("stream.tooltip7")}
        >
          {isVideo ? (
            <ScreenShareOutlinedIcon className="icon" />
          ) : (
            <StopScreenShareOutlinedIcon className="icon" />
          )}
        </IconButton>
      )}

      {/* Toggle Camera */}
      {isJoined && (
        <IconButton
          title={video ? t("stream.tooltip8") : t("stream.tooltip9")}
          onClick={disableVideo}
          className="btn"
          disabled={video === null}
        >
          {video ? <VideocamIcon className="icon" /> : <VideocamOffIcon className="icon" />}
        </IconButton>
      )}

      {/* Mute/Unmute */}
      {isJoined && (
        <IconButton
          title={isMute ? t("stream.tooltip10") : t("stream.tooltip11")}
          onClick={mute}
          className="btn"
        >
          {isMute ? <MicOffIcon className="icon" /> : <MicIcon className="icon" />}
        </IconButton>
      )}

      {/* Device Dropdown */}
      {isJoined && (
        <Stack>
          <IconButton
            className="btn"
            onClick={handleClick}
            title={t("stream.tooltip12")}
            sx={{ 
              backgroundColor: anchorEl ? '#4CAF50' : '#353535' // Green when menu should be open
            }}
          >
            <SettingsOutlinedIcon className="_cp icon" color="default" />
          </IconButton>
          {/* Debug indicator */}
          {anchorEl && (
            <Box sx={{ 
              position: 'absolute', 
              top: -10, 
              right: -10, 
              width: 10, 
              height: 10, 
              backgroundColor: 'red', 
              borderRadius: '50%' 
            }} />
          )}
          <Portal>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              transformOrigin={{ vertical: "bottom", horizontal: "center" }}
              slotProps={{
                paper: {
                  style: {
                    backgroundColor: '#2A2A2A',
                    color: '#ffffff',
                    border: '1px solid #444',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    minWidth: 200,
                    zIndex: 99999,
                  },
                }
              }}
              sx={{
                zIndex: 99999,
                '& .MuiMenuItem-root': {
                  color: '#ffffff',
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: '#444',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#555',
                  },
                },
              }}
            >
            <MenuItem onClick={handleClose}>Test Menu Item</MenuItem>
            {/* Microphones */}
            {availableMics.length > 0 ? (
              availableMics.map((mic, index) => (
                <MenuItem
                  key={index}
                  selected={mic.label === currentMic?.label}
                  onClick={() => handleSwitchAudio(mic.label)}
                >
                  🎤 {mic.label}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No microphones available</MenuItem>
            )}
            <Divider sx={{ my: 0.5, borderColor: '#666' }} />
            {/* Cameras */}
            {availableCameras.length > 0 ? (
              availableCameras.map((camera, index) => (
                <MenuItem
                  key={index}
                  selected={camera.label === currentCamera?.label}
                  onClick={() => handleSwitchVideo(camera.label)}
                >
                  📷 {camera.label}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No cameras available</MenuItem>
            )}
            </Menu>
          </Portal>
        </Stack>
      )}
    </Stack>
  );

  // Render based on role
  if (role === "host" || role === "cohost") {
    return (
      <Box id="_streaming_control" className="_streaming_control" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        {renderHostOrCohostControls()}
      </Box>
    );
  }

  // Audience controls
  console.log('[StreamingControls] Rendering audience controls with:', { hostId, playlistId, role, isOwner });
  return (
    <Stack
      className="ml_auto streaming_control_box"
      alignItems="center"
      direction="row"
      spacing={1}
      sx={{ position: "fixed", right: "11%", bottom: "10px" }}
    >
      {/* Song Request Button for Viewers */}
      <IconButton 
        className="btn"
        onClick={() => setOpenAudienceSongRequest(true)}
        title="Request Song"
        sx={{ width: 48, height: 48 }}
      >
        <img 
          src={Images.jukebox} 
          className="icon" 
          alt="request song" 
          style={{ 
            filter: 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)',
            width: 24,
            height: 24
          }}
        />
      </IconButton>

      {/* Picture in Picture Button */}
      <IconButton
        onClick={() => {
          console.log("[StreamingControls] Picture-in-Picture clicked");
          // Get the video element
          const videoElements = document.querySelectorAll('video');
          if (videoElements.length > 0) {
            const mainVideo = videoElements[0]; // Get the first video element
            if (document.pictureInPictureElement) {
              // Exit PiP if already active
              document.exitPictureInPicture()
                .catch(err => console.error("Error exiting PiP:", err));
            } else if (mainVideo && mainVideo.requestPictureInPicture) {
              // Enter PiP
              mainVideo.requestPictureInPicture()
                .catch(err => console.error("Error entering PiP:", err));
            }
          }
        }}
        className="btn"
        title="Picture in Picture"
        sx={{ width: 48, height: 48 }}
      >
        <PictureInPictureIcon className="icon" sx={{ fontSize: 24 }} />
      </IconButton>

      {/* Follow Button */}
      <IconButton
        onClick={() => {
          console.log("[StreamingControls] Follow host clicked");
          if (onFollowHost) onFollowHost();
        }}
        className="btn"
        title="Follow"
        sx={{ width: 48, height: 48 }}
      >
        <PersonAddIcon className="icon" sx={{ fontSize: 24 }} />
      </IconButton>

      {/* Subscribe Button */}
      <IconButton
        onClick={() => {
          console.log("[StreamingControls] Subscribe to host clicked");
          if (onSubscribeHost) onSubscribeHost();
        }}
        className="btn"
        title="Subscribe"
        sx={{ width: 62, height: 62 }}
      >
        <img 
          src={Images.subscribe} 
          className="icon" 
          alt="subscribe" 
          style={{ 
            filter: 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)',
            width: 31,
            height: 31
          }}
        />
      </IconButton>

      {/* Song Request Dialog for audience */}
      {openAudienceSongRequest && (
        <SongRequestButton 
          hostId={hostId}
          roomId={playlistId}
          isOwner={false}
          open={openAudienceSongRequest}
          onClose={() => setOpenAudienceSongRequest(false)}
        />
      )}
    </Stack>
  );
}